import React, { useState } from "react";
import { Link } from "react-router";
import IconButton from "@material-ui/core/IconButton";
import ContentCopyIcon from "@material-ui/icons/ContentCopy";
import CheckIcon from "@material-ui/icons/Check";

const onlineBadgeStyle = (status) => ({
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: "4px",
  fontWeight: "bold",
  fontSize: "12px",
  color: "#fff",
  whiteSpace: "nowrap",
  backgroundColor: status === "Online" ? "#2e7d32" : "#c62828",
});

const center = { textAlign: "center" };

const substringFilter = (filter, row) =>
  String(row[filter.id] ?? "").toLowerCase().includes(filter.value.toLowerCase());

const CopyButton = ({ value }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(String(value));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <IconButton
      size="small"
      onClick={handleCopy}
      title="Copy"
      style={{ padding: "1px", color: copied ? "#2e7d32" : "#ccc" }}
    >
      {copied
        ? <CheckIcon style={{ fontSize: "12px" }} />
        : <ContentCopyIcon style={{ fontSize: "12px" }} />}
    </IconButton>
  );
};

const CopyRowButton = ({ row }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e) => {
    e.stopPropagation();
    const data = {
      device_id: row.device_id,
      member_id: row.member_id,
      status: row.status,
      controllable: row.controllable,
      panel_sn: row.panel_sn,
      online_status: row.online_status,
      command_status: row.command_status,
      soc_pct: row.soc_pct,
      open_errors: row.open_errors != null ? Number(row.open_errors) : 0,
    };
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <IconButton
      size="small"
      onClick={handleCopy}
      title="Copy row as JSON"
      style={{ padding: "2px", color: copied ? "#2e7d32" : "#bbb" }}
    >
      {copied
        ? <CheckIcon style={{ fontSize: "14px" }} />
        : <ContentCopyIcon style={{ fontSize: "14px" }} />}
    </IconButton>
  );
};

export const getBatteryTableColumns = (onOpenCommandHistory) => [
  {
    Header: "",
    id: "copy_row",
    width: 38,
    sortable: false,
    filterable: false,
    style: center,
    Cell: (row) => <CopyRowButton row={row.original} />,
  },
  {
    Header: "Device ID",
    accessor: "device_id",
    minWidth: 220,
    filterMethod: substringFilter,
    Cell: (row) => (
      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <span>{row.value || "—"}</span>
        {row.value && <CopyButton value={row.value} />}
      </div>
    ),
  },
  {
    Header: "Member ID",
    accessor: "member_id",
    minWidth: 210,
    filterMethod: substringFilter,
    Cell: (row) => (
      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
        {row.value
          ? <Link to={`/members/${row.value}/details`}>{row.value}</Link>
          : <span>—</span>}
        {row.value && <CopyButton value={row.value} />}
      </div>
    ),
  },
  {
    Header: "Status",
    accessor: "status",
    width: 95,
    headerStyle: center,
    style: center,
    filterMethod: substringFilter,
    Cell: (row) => row.value || "—",
  },
  {
    Header: "Controllable",
    accessor: "controllable",
    width: 90,
    headerStyle: center,
    style: center,
    filterMethod: (filter, row) =>
      (row[filter.id] ? "yes" : "no").includes(filter.value.toLowerCase()),
    Cell: (row) => (row.value ? "Yes" : "No"),
  },
  {
    Header: "Panel SN",
    accessor: "panel_sn",
    minWidth: 155,
    headerStyle: center,
    style: center,
    filterMethod: substringFilter,
    Cell: (row) => (
      <div style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
        <span>{row.value || "—"}</span>
        {row.value && <CopyButton value={row.value} />}
      </div>
    ),
  },
  {
    Header: "Online Status",
    accessor: "online_status",
    width: 115,
    headerStyle: center,
    style: center,
    filterMethod: (filter, row) =>
      (row[filter.id] || "Offline").toLowerCase().includes(filter.value.toLowerCase()),
    Cell: (row) => (
      <span style={onlineBadgeStyle(row.value)}>{row.value || "Offline"}</span>
    ),
  },
  {
    Header: "Command Status",
    accessor: "command_status",
    minWidth: 155,
    headerStyle: center,
    style: center,
    filterMethod: substringFilter,
    Cell: (row) => (
      <button
        type="button"
        title="View command history"
        onClick={(e) => {
          e.stopPropagation();
          onOpenCommandHistory(row.original);
        }}
        style={{
          fontFamily: "monospace",
          fontSize: "12px",
          color: "#3f51b5",
          textDecoration: "underline",
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
        }}
      >
        {row.value || "—"}
      </button>
    ),
  },
  {
    Header: "SOC %",
    accessor: "soc_pct",
    width: 80,
    headerStyle: center,
    style: center,
    filterMethod: substringFilter,
    Cell: (row) =>
      row.value != null ? `${Number(row.value).toFixed(1)}%` : "—",
  },
  {
    Header: "Open Errors",
    accessor: "open_errors",
    width: 92,
    headerStyle: center,
    style: center,
    filterMethod: substringFilter,
    Cell: (row) => (
      <span
        style={{
          fontWeight: row.value > 0 ? "bold" : "normal",
          color: row.value > 0 ? "#c62828" : "inherit",
        }}
      >
        {row.value ?? 0}
      </span>
    ),
  },
];
